import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    MessageCircle,
    Phone,
    Mail,
    Send,
    CheckCircle,
    Clock,
    User,
    FileText,
    Download,
    Search
} from 'lucide-react';

const CleanBackground = () => {
    return (
        <div className="fixed inset-0 overflow-hidden pointer-events-none bg-gray-50">
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gray-300 rounded-full transform rotate-12"></div>
                <div className="absolute top-1/3 right-1/3 w-40 h-40 bg-gray-300 rounded-full transform -rotate-6"></div>
                <div className="absolute bottom-1/3 left-1/2 w-36 h-36 bg-gray-300 rounded-full transform rotate-3"></div>
                <div className="absolute top-1/2 right-1/4 w-28 h-28 bg-gray-300 rounded-full transform -rotate-12"></div>
            </div>
        </div>
    );
};

export default function Support() {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const supportCategories = [
        { id: 'all', label: 'All Topics', count: 45 },
        { id: 'account', label: 'Account', count: 12 },
        { id: 'investment', label: 'Investment', count: 15 },
        { id: 'technical', label: 'Technical', count: 8 },
        { id: 'billing', label: 'Billing', count: 10 }
    ];

    const quickActions = [
        {
            title: "Contact Support",
            description: "Get help from our support team",
            icon: MessageCircle,
            color: "green",
            action: "contact"
        },
        {
            title: "Report Issue",
            description: "Report bugs or technical issues",
            icon: FileText,
            color: "blue",
            action: "report"
        },
        {
            title: "Request Onboarding",
            description: "Get help with account setup",
            icon: User,
            color: "purple",
            action: "onboard"
        },
        {
            title: "Download Resources",
            description: "Access guides and documentation",
            icon: Download,
            color: "orange",
            action: "download"
        }
    ];

    const faqItems = [
        {
            id: 1,
            question: "How do I create an account?",
            answer: "Creating an account is simple. Click the 'Sign Up' button, provide your email address, and follow the verification process. You'll be guided through the onboarding steps.",
            category: "account",
            helpful: 89
        },
        {
            id: 2,
            question: "What is the minimum investment amount?",
            answer: "You can start investing with as little as $100. This makes clean energy investment accessible to everyone while maintaining project viability.",
            category: "investment",
            helpful: 94
        },
        {
            id: 3,
            question: "How do I track my investments?",
            answer: "Use our dashboard to monitor your investments in real-time. You can see returns, environmental impact, and project performance all in one place.",
            category: "investment",
            helpful: 91
        },
        {
            id: 4,
            question: "Is my investment secure?",
            answer: "Yes, we use bank-level security and blockchain technology to ensure your investments are secure and transparent.",
            category: "technical",
            helpful: 87
        },
        {
            id: 5,
            question: "How are returns calculated?",
            answer: "Returns are calculated based on actual energy production and current market rates. We provide transparent, real-time tracking of your investment performance.",
            category: "investment",
            helpful: 92
        },
        {
            id: 6,
            question: "Can I withdraw my investment early?",
            answer: "Investments are typically locked for the project duration to ensure stability. However, we offer secondary market options for some investments.",
            category: "investment",
            helpful: 85
        }
    ];

    const filteredFaq = faqItems.filter(item => {
        const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
        const matchesSearch = searchQuery === '' ||
            item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.answer.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const contactMethods = [
        {
            title: "Live Chat",
            description: "Get instant help from our support team",
            icon: MessageCircle,
            availability: "24/7",
            responseTime: "< 2 min"
        },
        {
            title: "Email Support",
            description: "Send us a detailed message",
            icon: Mail,
            availability: "24/7",
            responseTime: "< 4 hours"
        },
        {
            title: "Phone Support",
            description: "Speak directly with our team",
            icon: Phone,
            availability: "Mon-Fri 9AM-6PM",
            responseTime: "Immediate"
        }
    ];

    const handleQuickAction = (action: string) => {
        switch (action) {
            case 'contact':
                // Scroll to contact section
                document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                break;
            case 'report':
                // Open issue reporting modal
                console.log('Open issue reporting');
                break;
            case 'onboard':
                // Open onboarding request
                console.log('Open onboarding request');
                break;
            case 'download':
                // Open resources download
                console.log('Open resources download');
                break;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <CleanBackground />

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                {/* Header Section */}
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                            How Can We <span className="text-green-600">Help?</span>
                        </h1>
                        <p className="text-lg max-w-3xl mx-auto text-gray-600 mb-8">
                            Minimal friction support. 1 click to contact, 1 click to onboard, 1 click to report.
                            Get the help you need quickly and efficiently.
                        </p>
                    </motion.div>

                    {/* Search Bar */}
                    <motion.div
                        className="max-w-2xl mx-auto mb-8"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                    >
                        <div className="relative">
                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search for help..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                            />
                        </div>
                    </motion.div>
                </div>

                {/* Quick Actions */}
                <motion.div
                    className="mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                >
                    <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Quick Actions</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto items-stretch">
                        {quickActions.map((action, index) => {
                            const Icon = action.icon;
                            return (
                                <motion.button
                                    key={action.title}
                                    onClick={() => handleQuickAction(action.action)}
                                    className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 text-center hover:shadow-xl transition-all duration-200 group h-full flex flex-col"
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.6 + index * 0.1, duration: 0.6 }}
                                    whileHover={{ y: -5, scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 mx-auto transition-colors duration-200 ${action.color === 'green' ? 'bg-green-100 group-hover:bg-green-200' :
                                        action.color === 'blue' ? 'bg-blue-100 group-hover:bg-blue-200' :
                                            action.color === 'purple' ? 'bg-purple-100 group-hover:bg-purple-200' : 'bg-orange-100 group-hover:bg-orange-200'
                                        }`}>
                                        <Icon className={`w-6 h-6 transition-colors duration-200 ${action.color === 'green' ? 'text-green-600 group-hover:text-green-700' :
                                            action.color === 'blue' ? 'text-blue-600 group-hover:text-blue-700' :
                                                action.color === 'purple' ? 'text-purple-600 group-hover:text-purple-700' : 'text-orange-600 group-hover:text-orange-700'
                                            }`} />
                                    </div>
                                    <div className="flex-1 flex flex-col justify-start text-center">
                                        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-gray-800 transition-colors duration-200 leading-tight">{action.title}</h3>
                                        <p className="text-gray-600 text-sm leading-relaxed">{action.description}</p>
                                    </div>
                                </motion.button>
                            );
                        })}
                    </div>
                </motion.div>

                {/* FAQ Section */}
                <motion.div
                    className="mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.6 }}
                >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 md:mb-0">Frequently Asked Questions</h2>

                        {/* Category Filter */}
                        <div className="flex flex-wrap gap-2">
                            {supportCategories.map((category) => (
                                <button
                                    key={category.id}
                                    onClick={() => setSelectedCategory(category.id)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${selectedCategory === category.id
                                        ? 'bg-green-600 text-white'
                                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                        }`}
                                >
                                    {category.label} ({category.count})
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-4">
                        {filteredFaq.map((faq, index) => (
                            <motion.div
                                key={faq.id}
                                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.0 + index * 0.1, duration: 0.6 }}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h3>
                                        <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                                    </div>
                                    <div className="ml-4 flex items-center space-x-2">
                                        <span className="text-sm text-gray-500">{faq.helpful}% helpful</span>
                                        <button className="text-green-600 hover:text-green-700">
                                            <CheckCircle className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Contact Methods */}
                <motion.div
                    id="contact-section"
                    className="mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2, duration: 0.6 }}
                >
                    <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Contact Us</h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {contactMethods.map((method, index) => {
                            const Icon = method.icon;
                            return (
                                <motion.div
                                    key={method.title}
                                    className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 text-center"
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1.4 + index * 0.1, duration: 0.6 }}
                                    whileHover={{ y: -5, scale: 1.02 }}
                                >
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Icon className="w-8 h-8 text-green-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">{method.title}</h3>
                                    <p className="text-gray-600 mb-4">{method.description}</p>
                                    <div className="space-y-2 text-sm text-gray-500">
                                        <div className="flex items-center justify-center">
                                            <Clock className="w-4 h-4 mr-2" />
                                            {method.availability}
                                        </div>
                                        <div className="flex items-center justify-center">
                                            <span className="font-medium">Response: {method.responseTime}</span>
                                        </div>
                                    </div>
                                    <button className="mt-4 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors">
                                        Contact Now
                                    </button>
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Contact Form */}
                <motion.div
                    className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.6, duration: 0.6 }}
                >
                    <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Send us a Message</h2>
                    <form className="max-w-2xl mx-auto">
                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    placeholder="Your name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                <input
                                    type="email"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    placeholder="your@email.com"
                                />
                            </div>
                        </div>
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                            <input
                                type="text"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="What can we help you with?"
                            />
                        </div>
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                            <textarea
                                rows={4}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Tell us more about your question or issue..."
                            ></textarea>
                        </div>
                        <div className="text-center">
                            <motion.button
                                type="submit"
                                className="bg-green-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center mx-auto"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Send Message <Send className="w-5 h-5 ml-2" />
                            </motion.button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </div>
    );
}
